import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const PREVIEW_ROWS = [
  ['positioning', { de: 'Positionierung', en: 'Positioning' }],
  ['usp', { de: 'Alleinstellung', en: 'Unique strength' }],
  ['tone', { de: 'Ton', en: 'Tone' }],
];

/**
 * Zustand und Aktionen des Personenprofils (Personal DNA). Wird einmal pro Seite verwendet, damit Übersicht, Tabs und
 * Speichern-Leiste denselben Stand sehen. Das Profil ist die einzige Quelle; die technische Brand-DNA-Struktur,
 * die der Rest der App liest, wird beim Speichern serverseitig daraus abgeleitet.
 */
export function usePersonalDna({ enabled, lang = 'de', onChanged }) {
  const { getAuthHeader } = useAuth();
  const T = (de, en) => (lang === 'de' ? de : en);

  const [profile, setProfile] = useState(null);
  const [voice, setVoice] = useState({});
  const [samples, setSamples] = useState(0);
  const [cvSources, setCvSources] = useState([]);
  const [dna, setDna] = useState(null);             // { adopted, needs_adoption, preview }
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);
  // Ungespeicherte Änderungen nicht durch ein Nachladen überschreiben
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  const load = useCallback(async () => {
    const h = { headers: getAuthHeader() };
    try {
      const [pbRes, dnaRes, voiceRes] = await Promise.all([
        axios.get(`${API}/projects/active/personal-brand`, h),
        axios.get(`${API}/projects/active/personal-dna`, h).catch(() => ({ data: null })),
        axios.get(`${API}/projects/active/voice`, h).catch(() => ({ data: null })),
      ]);
      const d = pbRes.data;
      const pb = d.personal_brand && Object.keys(d.personal_brand).length ? d.personal_brand : (d.personal_basics || {});
      const { voice: _voice, cv_sources: srcs, ...rest } = pb;      // Stimme und Lebenslauf-Quellen haben eigene Speicherpfade
      setProfile(prev => (prev && dirtyRef.current ? prev : { topics: [], story_angles: [], proof_points: [], avoid: [], ...rest }));
      setCvSources(srcs || []);
      setVoice(voiceRes.data?.voice || {});
      setSamples(voiceRes.data?.samples?.length || 0);
      setDna(dnaRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Profil konnte nicht geladen werden', 'Could not load the profile'));
    } finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  const update = (p) => { setProfile(p); setDirty(true); };
  const err = (e, fallback) => toast.error(e.response?.data?.detail || fallback);
  const headers = getAuthHeader();

  const save = async () => {
    setBusy('save');
    try {
      const cleaned = { ...profile, topics: (profile.topics || []).filter(t => t.title?.trim()) };
      delete cleaned.voice;
      delete cleaned.cv_sources;
      const { data: r } = await axios.put(`${API}/projects/active/personal-brand`, { personal_brand: cleaned, language: lang }, { headers });
      const { voice: _v, cv_sources: _c, ...saved } = r.personal_brand || {};
      setProfile({ topics: [], story_angles: [], proof_points: [], avoid: [], ...saved });
      setDirty(false);
      toast.success(T('Gespeichert', 'Saved'));
      onChanged && onChanged();
    } catch (e) { err(e, T('Speichern fehlgeschlagen', 'Saving failed')); } finally { setBusy(''); }
  };

  // Website einer Beschäftigung zuordnen: wird gelesen, analysiert und als Quelle an die Rolle gehängt
  const addRoleWebsite = async (roleId, url) => {
    try {
      const { data: r } = await axios.post(`${API}/projects/active/roles/${roleId}/website`, { url, language: lang }, { headers, timeout: 150000 });
      toast.success(T('Website gelesen und zugeordnet', 'Website read and assigned'));
      return r.source;
    } catch (e) { err(e, T('Die Website konnte nicht gelesen werden', 'Could not read the website')); return null; }
  };

  // Profil-Entwurf nachholen (Onboarding abgebrochen oder fehlgeschlagen): füllt nur Leeres
  const completeDraft = async () => {
    setBusy('draft');
    try {
      const { data: r } = await axios.post(`${API}/projects/active/personal-brand/complete-draft`, {}, { headers, timeout: 120000 });
      toast.success(r.changed?.length ? T('Entwurf erstellt. Bitte prüfe ihn.', 'Draft created. Please review it.') : T('Es gab nichts zu ergänzen.', 'Nothing to add.'));
      await load();
      onChanged && onChanged();
    } catch (e) { err(e, T('Entwurf konnte nicht erstellt werden', 'Could not create the draft')); } finally { setBusy(''); }
  };

  // Ältere Personen-Projekte: Firmen-Identität durch die aus dem Profil abgeleiteten Werte ersetzen (Optik bleibt)
  const adopt = async () => {
    setBusy('adopt');
    try {
      await axios.post(`${API}/projects/active/personal-dna/adopt`, {}, { headers });
      toast.success(T('Auf Personal DNA umgestellt', 'Switched to Personal DNA'));
      await load();
      onChanged && onChanged();
    } catch (e) { err(e, T('Umstellen fehlgeschlagen', 'Switching failed')); } finally { setBusy(''); }
  };

  return { profile, voice, samples, cvSources, dna, dirty, busy, loading, update, save, addRoleWebsite, completeDraft, adopt, reload: load };
}

/** Hinweise, die über allem stehen: Umstellen auf Personal DNA (ältere Projekte) und fehlender Entwurf. */
export const DnaNotices = ({ s, lang = 'de' }) => {
  const T = (de, en) => (lang === 'de' ? de : en);
  if (!s.profile) return null;
  const noTopics = !(s.profile.topics || []).some(t => t.title?.trim());
  return (
    <div className="space-y-3 mb-4">
      {s.dna?.needs_adoption && (
        <div className="rounded-xl border border-[#F5A524]/40 bg-[#F5A524]/10 p-4" data-testid="dna-adopt-banner">
          <p className="text-white text-sm font-medium">{T('Deine DNA stammt noch von der Firma', 'Your DNA still comes from the company')}</p>
          <p className="text-xs text-[#A0AEC0] mt-1">{T('Wir können sie jetzt aus deinem Profil ableiten. Optik (Farben, Logo, Bilder) bleibt. Identität, Zielgruppen, Stimme und Regeln der Firma werden ersetzt.', 'We can now derive it from your profile. Visuals (colours, logo, images) stay. Identity, audiences, voice and rules of the company are replaced.')}</p>
          {s.dna.preview && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs" data-testid="dna-adopt-preview">
              {[['current', T('Bisher', 'Currently')], ['next', T('Danach', 'After')]].map(([k, label]) => (
                <div key={k} className="rounded-lg bg-[#0A1628] p-3">
                  <p className="text-[11px] uppercase tracking-wide text-[#6B7C90] mb-1">{label}</p>
                  {PREVIEW_ROWS.map(([f, names]) => (
                    <p key={f} className="text-[#C5D0DD] mb-0.5"><span className="text-[#6B7C90]">{names[lang] || names.de}: </span>{s.dna.preview[k][f] || '—'}</p>
                  ))}
                  <p className="text-[#C5D0DD]"><span className="text-[#6B7C90]">{T('Zielgruppen / Themen: ', 'Audiences / topics: ')}</span>{s.dna.preview[k].personas} / {s.dna.preview[k].themes}</p>
                </div>
              ))}
            </div>
          )}
          <Button size="sm" onClick={s.adopt} disabled={!!s.busy || s.dirty} className="mt-3 bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628]" data-testid="dna-adopt">
            {s.busy === 'adopt' && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}{T('Auf Personal DNA umstellen', 'Switch to Personal DNA')}
          </Button>
          {s.dirty && <p className="text-[11px] text-[#F5A524] mt-1.5">{T('Speichere zuerst deine Änderungen.', 'Save your changes first.')}</p>}
        </div>
      )}
      {noTopics && (
        <div className="rounded-xl border border-[#00D9C8]/40 bg-[#00D9C8]/5 p-4" data-testid="draft-banner">
          <p className="text-white text-sm font-medium">{T('Dein Profil-Entwurf fehlt noch', 'Your profile draft is missing')}</p>
          <p className="text-xs text-[#A0AEC0] mt-1">{T('Aus deinen Angaben entstehen Positionierung, Zielgruppe und Themen. Was du schon eingetragen hast, bleibt unverändert.', 'Positioning, audience and topics are drafted from your details. Anything you already entered stays as it is.')}</p>
          <Button size="sm" onClick={s.completeDraft} disabled={!!s.busy} className="mt-3 bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628]" data-testid="draft-create">
            {s.busy === 'draft' && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}{T('Entwurf erstellen', 'Create draft')}
          </Button>
        </div>
      )}
    </div>
  );
};

/** Leiste mit „Speichern“, bleibt beim Scrollen sichtbar, solange es ungespeicherte Änderungen gibt. */
export const DnaSaveBar = ({ s, lang = 'de' }) => {
  const T = (de, en) => (lang === 'de' ? de : en);
  if (!s.dirty) return null;
  return (
    <div className="sticky top-2 z-20 mb-4 flex items-center justify-between gap-3 rounded-xl border border-[#00D9C8]/40 bg-[#0A1628]/95 backdrop-blur px-4 py-2.5 shadow-lg" data-testid="dna-save-bar">
      <p className="text-sm text-white">{T('Du hast ungespeicherte Änderungen.', 'You have unsaved changes.')}</p>
      <Button size="sm" onClick={s.save} disabled={!!s.busy} className="bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628]" data-testid="profile-save">
        {s.busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}{T('Speichern', 'Save')}
      </Button>
    </div>
  );
};
